import { FontFamily } from "@redotech/redo-content-builder-common/element-schema";
import type { FulfillmentDocumentTeam } from "../team-contracts";
import type { PoTemplate } from "./po-definition";

// Reuse shared layout types from packing slips
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  AlignItems,
  AlignItemsDisplayToCssValue,
  Alignment,
  PaddingSize,
  PageSize,
  type Padding,
} from "../packing-slips/packing-slip-builder";

export enum PoFontWeight {
  NORMAL = "normal",
  BOLD = "bold",
}

export const FONT_WEIGHTS = [PoFontWeight.NORMAL, PoFontWeight.BOLD] as const;

export const FONT_FAMILIES = [
  FontFamily.ARIAL,
  FontFamily.COURIER_NEW,
  FontFamily.GEORGIA,
  FontFamily.LUCIDA_SANS_UNICODE,
  FontFamily.TAHOMA,
  FontFamily.TIMES_NEW_ROMAN,
  FontFamily.TREBUCHET_MS,
  FontFamily.VERDANA,
] as const;

export enum PoBlockType {
  // Company information
  COMPANY_NAME = "company-name",
  COMPANY_LOGO = "company-logo",
  COMPANY_ADDRESS = "company-address",
  COMPANY_CONTACT = "company-contact",

  // Supplier information
  SUPPLIER_NAME = "supplier-name",
  SUPPLIER_ADDRESS = "supplier-address",
  SUPPLIER_CONTACT = "supplier-contact",

  // PO header
  PO_HEADER = "po-header",
  PO_NUMBER = "po-number",
  CREATION_DATE = "creation-date",
  EXPECTED_DELIVERY_DATE = "expected-delivery-date",
  PAYMENT_TERMS = "payment-terms",
  SHIP_TO_ADDRESS = "ship-to-address",

  // Line items
  LINE_ITEMS_TABLE = "line-items-table",

  // Shipments
  SHIPMENTS_TABLE = "shipments-table",

  // Summary
  PO_SUMMARY = "po-summary",

  // Notes
  NOTES_FIELD = "notes-field",

  // Generic / layout
  TEXT = "text",
  IMAGE = "image",
  SPACER = "spacer",
  LINE = "line",
  COLUMN = "column",
}

export enum PoBlockCategory {
  GENERAL = "general",
  COMPANY = "company",
  SUPPLIER = "supplier",
  PO_HEADER = "po-header",
  LINE_ITEMS = "line-items",
  SUMMARY = "summary",
  NOTES = "notes",
}

export const poBlockCategoryDisplayNames: Record<PoBlockCategory, string> = {
  [PoBlockCategory.GENERAL]: "General",
  [PoBlockCategory.COMPANY]: "Company Information",
  [PoBlockCategory.SUPPLIER]: "Supplier Information",
  [PoBlockCategory.PO_HEADER]: "PO Header",
  [PoBlockCategory.LINE_ITEMS]: "Line Items",
  [PoBlockCategory.SUMMARY]: "Summary",
  [PoBlockCategory.NOTES]: "Notes",
};

export const poBlockCategories: Record<PoBlockCategory, PoBlockType[]> = {
  [PoBlockCategory.COMPANY]: [
    PoBlockType.COMPANY_NAME,
    PoBlockType.COMPANY_LOGO,
    PoBlockType.COMPANY_ADDRESS,
    PoBlockType.COMPANY_CONTACT,
  ],
  [PoBlockCategory.SUPPLIER]: [
    PoBlockType.SUPPLIER_NAME,
    PoBlockType.SUPPLIER_ADDRESS,
    PoBlockType.SUPPLIER_CONTACT,
  ],
  [PoBlockCategory.PO_HEADER]: [
    PoBlockType.PO_HEADER,
    PoBlockType.PO_NUMBER,
    PoBlockType.CREATION_DATE,
    PoBlockType.EXPECTED_DELIVERY_DATE,
    PoBlockType.PAYMENT_TERMS,
    PoBlockType.SHIP_TO_ADDRESS,
  ],
  [PoBlockCategory.LINE_ITEMS]: [
    PoBlockType.LINE_ITEMS_TABLE,
    PoBlockType.SHIPMENTS_TABLE,
  ],
  [PoBlockCategory.SUMMARY]: [PoBlockType.PO_SUMMARY],
  [PoBlockCategory.NOTES]: [PoBlockType.NOTES_FIELD],
  [PoBlockCategory.GENERAL]: [
    PoBlockType.TEXT,
    PoBlockType.IMAGE,
    PoBlockType.SPACER,
    PoBlockType.LINE,
    PoBlockType.COLUMN,
  ],
};

// Line items table column types
export enum PoLineItemColumnType {
  SKU = "sku",
  PRODUCT_NAME = "productName",
  VARIANT = "variant",
  QUANTITY = "quantity",
  UNIT_COST = "unitCost",
  TOTAL_COST = "totalCost",
  UPC = "upc",
}

export interface PoLineItemTableColumn {
  type: PoLineItemColumnType;
  enabled: boolean;
  title?: string | null;
}

export const poLineItemColumnTypeToLabel: Record<PoLineItemColumnType, string> =
  {
    [PoLineItemColumnType.SKU]: "SKU",
    [PoLineItemColumnType.PRODUCT_NAME]: "Product Name",
    [PoLineItemColumnType.VARIANT]: "Variant",
    [PoLineItemColumnType.QUANTITY]: "Quantity",
    [PoLineItemColumnType.UNIT_COST]: "Unit Cost",
    [PoLineItemColumnType.TOTAL_COST]: "Total Cost",
    [PoLineItemColumnType.UPC]: "UPC",
  };

// Shipments table column types
export enum PoShipmentColumnType {
  ESTIMATED_ARRIVAL = "estimatedArrival",
  SHIPMENT_ID = "shipmentId",
  DESTINATION = "destination",
  TRACKING_NUMBER = "trackingNumber",
  DUTY_COST = "dutyCost",
}

export interface PoShipmentTableColumn {
  type: PoShipmentColumnType;
  enabled: boolean;
  title?: string | null;
}

export const poShipmentColumnTypeToLabel: Record<PoShipmentColumnType, string> =
  {
    [PoShipmentColumnType.ESTIMATED_ARRIVAL]: "Estimated Arrival",
    [PoShipmentColumnType.SHIPMENT_ID]: "Shipment ID",
    [PoShipmentColumnType.DESTINATION]: "Destination",
    [PoShipmentColumnType.TRACKING_NUMBER]: "Tracking Number",
    [PoShipmentColumnType.DUTY_COST]: "Duty Cost",
  };

// Section namespace — discriminated union for all PO block types
export namespace PoSection {
  export interface BaseSection {
    blockId: string;
    sectionPadding: {
      top: number;
      right: number;
      bottom: number;
      left: number;
    };
    sectionColor?: string | null;
  }

  export interface FontSection extends BaseSection {
    fontFamily?: string | null;
    fontSize?: number | null;
    textColor?: string | null;
    fontWeight?: PoFontWeight | null;
    textAlign?: "left" | "center" | "right" | null;
  }

  export interface AddressSection extends FontSection {
    showHeader?: boolean | null;
    header?: string | null;
    showName?: boolean | null;
    boldName?: boolean | null;
    showStreet?: boolean | null;
    showCity?: boolean | null;
    showState?: boolean | null;
    showPostalCode?: boolean | null;
    showCountry?: boolean | null;
    showPhone?: boolean | null;
    showEmail?: boolean | null;
  }

  // Company blocks
  export interface CompanyName extends FontSection {
    type: PoBlockType.COMPANY_NAME;
  }

  export interface CompanyLogo extends BaseSection {
    type: PoBlockType.COMPANY_LOGO;
    imageUrl?: string | null;
    maxHeight?: number | null;
  }

  export interface CompanyAddress extends AddressSection {
    type: PoBlockType.COMPANY_ADDRESS;
  }

  export interface CompanyContact extends FontSection {
    type: PoBlockType.COMPANY_CONTACT;
    showPhone: boolean;
    showEmail: boolean;
  }

  // Supplier blocks
  export interface SupplierName extends FontSection {
    type: PoBlockType.SUPPLIER_NAME;
  }

  export interface SupplierAddress extends AddressSection {
    type: PoBlockType.SUPPLIER_ADDRESS;
  }

  export interface SupplierContact extends FontSection {
    type: PoBlockType.SUPPLIER_CONTACT;
    showPhone: boolean;
    showEmail: boolean;
  }

  // PO header blocks
  export interface PoHeader extends FontSection {
    type: PoBlockType.PO_HEADER;
    dateFormat?: string | null;
  }

  export interface PoNumber extends FontSection {
    type: PoBlockType.PO_NUMBER;
    labelPrefix?: string | null;
  }

  export interface CreationDate extends FontSection {
    type: PoBlockType.CREATION_DATE;
    dateFormat?: string | null;
  }

  export interface ExpectedDeliveryDate extends FontSection {
    type: PoBlockType.EXPECTED_DELIVERY_DATE;
    dateFormat?: string | null;
  }

  export interface PaymentTerms extends FontSection {
    type: PoBlockType.PAYMENT_TERMS;
  }

  export interface ShipToAddress extends AddressSection {
    type: PoBlockType.SHIP_TO_ADDRESS;
  }

  // Line items
  export interface LineItemsTable extends FontSection {
    type: PoBlockType.LINE_ITEMS_TABLE;
    tableColumns: PoLineItemTableColumn[];
    includeHeader?: boolean | null;
    headerBackgroundColor?: string | null;
    showSectionHeader?: boolean | null;
    sectionHeader?: string | null;
    tableBorderColor?: string | null;
    tableBorderWidth?: number | null;
    rowHeight?: number | null;
  }

  // Shipments table
  export interface ShipmentsTable extends FontSection {
    type: PoBlockType.SHIPMENTS_TABLE;
    tableColumns: PoShipmentTableColumn[];
    includeHeader?: boolean | null;
    tableBorderColor?: string | null;
    tableBorderWidth?: number | null;
    rowHeight?: number | null;
  }

  // Summary
  export interface PoSummary extends FontSection {
    type: PoBlockType.PO_SUMMARY;
    showSubtotal: boolean;
    showTax: boolean;
    showShipping: boolean;
    showTotal: boolean;
    showCurrency: boolean;
  }

  // Notes
  export interface NotesField extends FontSection {
    type: PoBlockType.NOTES_FIELD;
    showHeader?: boolean | null;
    header?: string | null;
  }

  // Generic blocks
  export interface Text extends BaseSection {
    type: PoBlockType.TEXT;
    textColor: string;
    fontSize: number;
    fontFamily: string;
    linkColor: string;
    text: string;
  }

  export interface Image extends BaseSection {
    type: PoBlockType.IMAGE;
    imageUrl: string;
    showCaption: boolean;
    maxHeight?: number | null;
    caption?: string | null;
    altText?: string | null;
  }

  export interface Spacer extends BaseSection {
    type: PoBlockType.SPACER;
    height: number;
  }

  export interface Line extends BaseSection {
    type: PoBlockType.LINE;
    color: string;
  }

  export interface Column extends BaseSection {
    type: PoBlockType.COLUMN;
    columns: (PoSectionWithoutColumn | null)[];
    columnCount: number;
    gap: number;
  }
}

export type PoSectionWithoutColumn =
  | PoSection.CompanyName
  | PoSection.CompanyLogo
  | PoSection.CompanyAddress
  | PoSection.CompanyContact
  | PoSection.SupplierName
  | PoSection.SupplierAddress
  | PoSection.SupplierContact
  | PoSection.PoHeader
  | PoSection.PoNumber
  | PoSection.CreationDate
  | PoSection.ExpectedDeliveryDate
  | PoSection.PaymentTerms
  | PoSection.ShipToAddress
  | PoSection.LineItemsTable
  | PoSection.ShipmentsTable
  | PoSection.PoSummary
  | PoSection.NotesField
  | PoSection.Text
  | PoSection.Image
  | PoSection.Spacer
  | PoSection.Line;

export type PoSectionType = PoSectionWithoutColumn | PoSection.Column;

// Data injected into block renderers at render time
export interface PoSharedProps {
  team: FulfillmentDocumentTeam;
  template: Omit<PoTemplate, "sections">;
  purchaseOrderData: PurchaseOrderRenderData;
  isBuilder: boolean;
  blockId?: string;
  onUpdate?: (updater: (section: PoSectionType) => PoSectionType) => void;
}

// Shape of PO data available to block renderers
export interface PurchaseOrderRenderData {
  poNumber: string;
  reference?: string | null;
  currency: string;
  noteToSupplier?: string | null;
  createdAt: string;
  orderDate: string;
  expectedDeliveryDate?: string | null;
  paymentTerms?: string | null;

  products: {
    sku: string | null;
    title: string;
    variant?: string | null;
    quantity: number;
    unitCost: number;
    upc?: string | null;
  }[];

  supplier?: {
    name: string;
    street1?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    postalCode?: string | null;
    email?: string | null;
    phone?: string | null;
  };

  shipToLocation?: {
    name: string;
    street1?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    phone?: string | null;
  };

  merchantInfo: {
    logoUrl?: string;
    name: string;
    street1?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    phone?: string | null;
    email?: string | null;
  };

  shipments?: {
    shipmentId: string;
    estimatedArrival?: string | null;
    destination?: string | null;
    trackingNumber?: string | null;
    dutyCost?: number | null;
    street1?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
  }[];

  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
}
